import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Theme,
  makeStyles,
} from '@material-ui/core';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useParams, useNavigate } from 'react-router-dom';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpExceptionsManagePermission } from '@internal/ksdp-permissions-common';
import { ExceptionCase, ExceptionMessage } from '../types';
import { capitalize, displayUser, initials } from '../utils';

const statusColors: Record<string, 'primary' | 'secondary' | 'default'> = {
  open: 'primary',
  approved: 'secondary',
  rejected: 'default',
  expired: 'default',
};

const useStyles = makeStyles((theme: Theme) => ({
  priorCaseNote: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5, 3),
    margin: theme.spacing(0, 0, 2),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  infoLabel: {
    margin: 0,
    marginBottom: 2,
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  infoValue: {
    margin: 0,
    fontSize: 14,
    wordBreak: 'break-word',
  },
  messages: {
    display: 'grid',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0.25),
  },
  message: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    maxWidth: '88%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  messageRequester: {
    justifySelf: 'end',
    flexDirection: 'row-reverse',
  },
  messageAdmin: {
    justifySelf: 'start',
  },
  avatar: {
    display: 'inline-flex',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    fontSize: 10,
    fontWeight: 700,
  },
  avatarRequester: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  avatarAdmin: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },
  bubble: {
    minWidth: 0,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
  },
  bubbleRequester: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  bubbleAdmin: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  bubbleMeta: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  bubbleAuthor: {
    fontSize: 12,
    fontWeight: 700,
  },
  bubbleTime: {
    fontSize: 11,
    whiteSpace: 'nowrap',
    opacity: 0.8,
  },
  bubbleText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
  },
}));

export const ExceptionCaseDetail = () => {
  const classes = useStyles();
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<ExceptionCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [replyText, setReplyText] = useState('');
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolving, setResolving] = useState(false);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const navigate = useNavigate();
  // UI-only gate; reply and resolve are enforced by the backend router.
  const { allowed: canManage } = usePermission({
    permission: ksdpExceptionsManagePermission,
  });

  const fetchCaseDetail = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('exceptions');
      const response = await fetchApi.fetch(`${baseUrl}/cases/${caseId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch case: ${response.statusText}`);
      }
      const data = await response.json();
      // The get handler wraps the case as { exceptionCase } for the requester
      // path; the admin route returns the serialized case directly. Accept both.
      setCaseData(data.exceptionCase ?? data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [caseId, discoveryApi, fetchApi]);

  useEffect(() => {
    if (caseId) {
      fetchCaseDetail();
    }
  }, [caseId, fetchCaseDetail]);

  const handleReply = async () => {
    if (!replyText.trim() || !caseData) return;

    try {
      const baseUrl = await discoveryApi.getBaseUrl('exceptions');
      const response = await fetchApi.fetch(
        `${baseUrl}/cases/${caseData.caseId}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: replyText }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to send reply');
      }

      setReplyText('');
      await fetchCaseDetail();
    } catch (err) {
      setError(err as Error);
    }
  };

  const handleResolve = async (decision: 'approve' | 'reject') => {
    if (!caseData) return;

    try {
      setResolving(true);
      const baseUrl = await discoveryApi.getBaseUrl('exceptions');
      const response = await fetchApi.fetch(
        `${baseUrl}/cases/${caseData.caseId}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ decision }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to resolve case');
      }

      setShowResolveDialog(false);
      await fetchCaseDetail();
    } catch (err) {
      setError(err as Error);
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (!caseData) {
    return <Typography>Case not found</Typography>;
  }

  const messages: ExceptionMessage[] = caseData.messages ?? [];

  return (
    <Box>
      <Button
        onClick={() => navigate('/exceptions')}
        style={{ marginBottom: 16 }}
      >
        ← Back to Cases
      </Button>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h5">Case {caseData.caseId}</Typography>
                <Chip
                  label={caseData.status}
                  size="small"
                  color={statusColors[caseData.status] || 'default'}
                />
              </Box>

              {caseData.priorCaseId && (
                <Typography variant="body2" className={classes.priorCaseNote}>
                  A previous case <strong>{caseData.priorCaseId}</strong>
                  {caseData.priorCaseStatus
                    ? ` (${capitalize(caseData.priorCaseStatus)})`
                    : ''}{' '}
                  was opened for this requirement. This new case is independent
                  — its conversation was not merged.
                </Typography>
              )}

              <Box component="dl" className={classes.infoGrid}>
                <div>
                  <dt className={classes.infoLabel}>Business Application</dt>
                  <dd className={classes.infoValue}>
                    {caseData.applicationName
                      ? `${caseData.applicationName} (${caseData.ban})`
                      : caseData.ban}
                  </dd>
                </div>
                <div>
                  <dt className={classes.infoLabel}>Requester</dt>
                  <dd className={classes.infoValue}>
                    {displayUser(caseData.requester)}
                  </dd>
                </div>
                <div>
                  <dt className={classes.infoLabel}>Opened</dt>
                  <dd className={classes.infoValue}>
                    {caseData.openedAt
                      ? new Date(caseData.openedAt).toLocaleString()
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className={classes.infoLabel}>Resolved by</dt>
                  <dd className={classes.infoValue}>
                    {caseData.resolvedBy
                      ? `${displayUser(caseData.resolvedBy)}${
                          caseData.resolvedAt
                            ? ` · ${new Date(
                                caseData.resolvedAt,
                              ).toLocaleString()}`
                            : ''
                        }`
                      : 'Not yet resolved'}
                  </dd>
                </div>
              </Box>

              <Divider style={{ margin: '16px 0' }} />

              <Typography variant="h6" gutterBottom>
                Conversation
              </Typography>

              <div className={classes.messages}>
                {messages.map((message, index) => {
                  const isAdmin = message.authorRole === 'admin';
                  return (
                    <article
                      key={`${message.postedAt}-${index}`}
                      className={`${classes.message} ${
                        isAdmin
                          ? classes.messageAdmin
                          : classes.messageRequester
                      }`}
                    >
                      <div
                        aria-hidden="true"
                        className={`${classes.avatar} ${
                          isAdmin
                            ? classes.avatarAdmin
                            : classes.avatarRequester
                        }`}
                      >
                        {initials(message.author)}
                      </div>
                      <div
                        className={`${classes.bubble} ${
                          isAdmin
                            ? classes.bubbleAdmin
                            : classes.bubbleRequester
                        }`}
                      >
                        <div className={classes.bubbleMeta}>
                          <span className={classes.bubbleAuthor}>
                            {displayUser(message.author)}
                            {isAdmin ? ' (Compliance Admin)' : ''}
                          </span>
                          <time
                            className={classes.bubbleTime}
                            dateTime={message.postedAt ?? undefined}
                          >
                            {message.postedAt
                              ? new Date(message.postedAt).toLocaleString()
                              : ''}
                          </time>
                        </div>
                        <p className={classes.bubbleText}>{message.body}</p>
                      </div>
                    </article>
                  );
                })}
              </div>

              {canManage && caseData.status === 'open' && (
                <Box mt={2}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label="Reply to this case"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    style={{ marginBottom: 16 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleReply}
                    disabled={!replyText.trim()}
                  >
                    Send Reply
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Case Details
              </Typography>

              <Box mb={2}>
                <Typography variant="subtitle2">Requirement</Typography>
                <Typography variant="body2">
                  {caseData.requirementRef}
                </Typography>
              </Box>

              {caseData.status === 'rejected' &&
                caseData.daysRemaining !== null && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Reopen window</Typography>
                    <Typography variant="body2">
                      {caseData.daysRemaining} day(s) remaining
                    </Typography>
                  </Box>
                )}

              {canManage && caseData.status === 'open' && (
                <Box mt={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={() => setShowResolveDialog(true)}
                    style={{ marginBottom: 8 }}
                  >
                    Resolve Case
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={canManage && showResolveDialog}
        onClose={() => setShowResolveDialog(false)}
      >
        <DialogTitle>Resolve Exception Case</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            How do you want to resolve this case?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResolveDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleResolve('approve')}
            color="primary"
            disabled={resolving}
          >
            Approve
          </Button>
          <Button
            onClick={() => handleResolve('reject')}
            color="secondary"
            disabled={resolving}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
